import { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Paper, Typography, Button, Alert } from "@mui/material";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import { useTranslation } from "react-i18next";
import axiosPublic from "../api/axiosPublic";

const COOLDOWN_SECONDS = 60;

export default function ConfirmEmail() {
    const { t } = useTranslation();
    const { state } = useLocation();
    const navigate = useNavigate();
    const [resent, setResent] = useState(false);
    const [error, setError] = useState('');
    const [cooldown, setCooldown] = useState(COOLDOWN_SECONDS);
    const timerRef = useRef(null);

    const email = state?.email ?? '';

    // Start cooldown immediately — the signup email was just sent on arrival.
    useEffect(() => {
        timerRef.current = setInterval(() => {
            setCooldown(s => {
                if (s <= 1) {
                    clearInterval(timerRef.current);
                    return 0;
                }
                return s - 1;
            });
        }, 1000);
        return () => clearInterval(timerRef.current);
    }, []);

    const handleResend = async () => {
        setError('');
        setResent(false);
        try {
            await axiosPublic.post("/api/app_user/resend-confirmation", { email });
            setResent(true);
            // Reset cooldown after a successful resend
            setCooldown(COOLDOWN_SECONDS);
            timerRef.current = setInterval(() => {
                setCooldown(s => {
                    if (s <= 1) {
                        clearInterval(timerRef.current);
                        return 0;
                    }
                    return s - 1;
                });
            }, 1000);
        } catch (err) {
            setError(err.response?.data?.error || t('auth.resend_failed'));
        }
    };

    return (
        <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "#f5f0eb" }}>
            <Paper elevation={3} sx={{ p: 5, maxWidth: 480, width: "100%", borderRadius: 4, textAlign: "center" }}>
                <MarkEmailReadIcon sx={{ fontSize: 64, color: "#C4622D", mb: 2 }} />
                <Typography variant="h5" fontWeight={700} mb={1} sx={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                    {t('auth.confirm_email_title')}
                </Typography>
                <Typography variant="body1" color="text.secondary" mb={3}>
                    {email
                        ? t('auth.confirm_email_body', { email })
                        : t('auth.confirm_email_body_generic')}
                </Typography>
                {resent && <Alert severity="success" sx={{ mb: 2, textAlign: "left" }}>{t('auth.resend_success')}</Alert>}
                {error && <Alert severity="error" sx={{ mb: 2, textAlign: "left" }}>{error}</Alert>}
                {email && (
                    <Button
                        variant="outlined"
                        fullWidth
                        onClick={handleResend}
                        disabled={cooldown > 0}
                        sx={{ mb: 2 }}
                    >
                        {cooldown > 0
                            ? t('auth.resend_cooldown', { seconds: cooldown })
                            : t('auth.resend_email')}
                    </Button>
                )}
                <Button variant="text" fullWidth component="a" href="/login">
                    {t('auth.back_to_login')}
                </Button>
            </Paper>
        </Box>
    );
}